<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">周边景区</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 个</span>
        <span class="divider">|</span>
        <span>展示中 <b>{{ statusCount(1) }}</b></span>
        <span class="divider">|</span>
        <span>已下架 <b>{{ statusCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input v-model="query.keyword" placeholder="景区名称/副标语关键词" clearable style="width: 180px" />
          </el-form-item>
          <el-form-item label="所属基地">
            <el-select v-model="query.base_id" placeholder="全部" clearable style="width: 180px">
              <el-option v-for="o in baseOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 120px">
              <el-option v-for="s in SCENIC_STATUS" :key="s.value" :label="s.label" :value="s.value" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增景区</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 排序 -->
        <el-table-column label="排序" width="70" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.sort"
              :min="0" :max="9999"
              size="mini"
              controls-position="right"
              class="sort-input"
              @change="val => handleSortChange(scope.row, val)"
            />
          </template>
        </el-table-column>
        <!-- 封面 -->
        <el-table-column label="封面" width="100" align="center">
          <template slot-scope="scope">
            <div v-if="scope.row.cover" class="cover-thumb">
              <el-image :src="scope.row.cover" :preview-src-list="[scope.row.cover]" fit="cover" style="width: 100%; height: 100%" />
            </div>
            <div v-else class="cover-thumb cover-thumb--empty">无图</div>
          </template>
        </el-table-column>
        <!-- 名称 + 等级 -->
        <el-table-column label="景区名称" min-width="140">
          <template slot-scope="scope">
            <span>{{ scope.row.name }}</span>
            <el-tag v-if="scope.row.level" size="mini" type="warning" effect="plain" class="level-tag">
              {{ scope.row.level }}级景区
            </el-tag>
          </template>
        </el-table-column>
        <!-- 所属基地 -->
        <el-table-column label="所属基地" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ baseLabel(scope.row.base_id) }}</template>
        </el-table-column>
        <!-- 类型 -->
        <el-table-column label="类型" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.scenery_type || '—' }}</template>
        </el-table-column>
        <!-- 地区 -->
        <el-table-column label="地区" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ regionText(scope.row) }}</template>
        </el-table-column>
        <!-- 开放时间 -->
        <el-table-column prop="open_time" label="开放时间" min-width="120" show-overflow-tooltip />
        <!-- 门票 -->
        <el-table-column label="门票" width="90" align="center">
          <template slot-scope="scope">{{ ticketText(scope.row.ticket_price) }}</template>
        </el-table-column>
        <!-- 状态 -->
        <el-table-column label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scenicStatusTagType(scope.row.status)" size="mini" effect="plain">
              {{ scenicStatusText(scope.row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 操作 -->
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button
              v-if="scope.row.status !== 1"
              type="text" size="mini"
              @click="handleStatus(scope.row, 1)"
            >展示</el-button>
            <el-button
              v-else
              type="text" size="mini" class="text-warning-btn"
              @click="handleStatus(scope.row, 0)"
            >下架</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 编辑/新增弹框 -->
    <el-dialog
      :title="isEdit ? '编辑景区' : '新增景区'"
      :visible.sync="formVisible"
      width="780px"
      top="4vh"
      class="scenic-form-dialog"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form ref="form" :model="form" :rules="rules" label-width="100px" label-position="top">
        <div class="form-section">
          <div class="form-section-title">基础</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="景区名称" prop="name">
                <el-autocomplete
                  v-model="form.name"
                  :fetch-suggestions="searchPlace"
                  :trigger-on-focus="false"
                  clearable
                  popper-class="place-suggestion-popper"
                  placeholder="输入景区名称搜索地图地点，选中自动带出省市与地址"
                  maxlength="128"
                  style="width: 100%"
                  @select="onPlaceSelect"
                >
                  <template slot-scope="{ item }">
                    <div class="place-suggestion">
                      <div class="ps-title"><span class="ps-pin"></span>{{ item.raw.title }}</div>
                      <div class="ps-addr">{{ item.raw.address }}</div>
                    </div>
                  </template>
                </el-autocomplete>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="所属基地" prop="base_id">
                <el-select v-model="form.base_id" placeholder="请选择" clearable style="width: 100%">
                  <el-option v-for="o in baseOptions" :key="o.value" :label="o.label" :value="o.value" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="景区等级" prop="level">
                <el-select v-model="form.level" placeholder="请选择（留空不展示等级）" clearable style="width: 100%">
                  <el-option v-for="l in levelOptions" :key="l.value" :label="l.label" :value="l.value" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="景区类型" prop="scenery_type">
                <el-select v-model="form.scenery_type" placeholder="请选择或输入" allow-create filterable style="width: 100%">
                  <el-option v-for="t in sceneryTypes" :key="t" :label="t" :value="t" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="8">
              <el-form-item label="省">
                <el-select v-model="form.province" placeholder="省" filterable clearable style="width: 100%" @change="onProvinceChange">
                  <el-option v-for="p in provinces" :key="p.code" :label="p.name" :value="p.code" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="市">
                <el-select v-model="form.city" placeholder="市" filterable clearable style="width: 100%" @change="onCityChange">
                  <el-option v-for="c in cities" :key="c.code" :label="c.name" :value="c.code" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="区/县">
                <el-select v-model="form.district" placeholder="区/县" filterable clearable style="width: 100%">
                  <el-option v-for="a in areas" :key="a.code" :label="a.name" :value="a.code" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="详细地址" prop="address">
            <el-input v-model="form.address" placeholder="选地图地点后自动填入，可改" maxlength="255" />
          </el-form-item>
          <el-form-item label="封面图" prop="cover">
            <image-uploader v-model="form.cover" />
            <span class="form-tip">列表卡缩略与详情页顶部大图共用</span>
          </el-form-item>
          <el-form-item label="副标语" prop="slogan">
            <el-input v-model="form.slogan" placeholder="详情大图上的那行字，如：山水相依 · 象鼻神技" maxlength="128" />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">游玩信息</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="开放时间" prop="open_time">
                <el-input v-model="form.open_time" placeholder="如：07:00 - 22:00" maxlength="64" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="门票价（元/人）" prop="ticket_price">
                <el-input-number v-model="form.ticket_price" :min="0" :max="99999" :precision="2" controls-position="right" style="width: 100%" />
                <span class="form-tip">填 0 显示为「免费」</span>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="最佳季节" prop="best_season">
                <el-input v-model="form.best_season" placeholder="如：春秋两季" maxlength="32" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="游玩时长" prop="play_duration">
                <el-input v-model="form.play_duration" placeholder="如：2-3小时 / 1-2天" maxlength="32" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="景区特色" prop="features">
            <el-select
              v-model="form.features"
              multiple filterable allow-create default-first-option
              :multiple-limit="6"
              placeholder="输入后回车添加，最多 6 个，如：山水相映"
              style="width: 100%"
            />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">图文介绍</div>
          <el-form-item label="景区介绍" prop="intro">
            <wechat-editor v-model="form.intro" />
          </el-form-item>
        </div>

        <div class="form-section">
          <div class="form-section-title">状态</div>
          <el-row :gutter="24">
            <el-col :span="12">
              <el-form-item label="排序值" prop="sort">
                <el-input-number v-model="form.sort" :min="0" :max="9999" controls-position="right" style="width: 100%" />
                <span class="form-tip">数字越小，列表越靠前</span>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="是否展示" prop="status">
                <el-radio-group v-model="form.status">
                  <el-radio :label="1">展示</el-radio>
                  <el-radio :label="0">下架</el-radio>
                </el-radio-group>
              </el-form-item>
            </el-col>
          </el-row>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  SCENIC_STATUS, scenicStatusText, scenicStatusTagType, ticketText
} from '@/mock/scenic'
import {
  getScenicList, createScenic, updateScenic,
  deleteScenic, setScenicStatus, setScenicSort, getStayBaseOptions
} from '@/api/stay'
import { getProvince, getCity, getArea, tencentAddressKeyword } from '@/api/public'
import { getDictMap } from '@/api/dict'
import ImageUploader from '@/components/ImageUploader.vue'
import WechatEditor from '@/components/WechatEditor.vue'

export default {
  name: 'ScenicManage',
  components: { ImageUploader, WechatEditor },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      query: { keyword: '', base_id: '', status: '' },
      formVisible: false,
      currentRow: null,
      submitting: false,
      form: this.emptyForm(),
      rules: {
        name: [{ required: true, message: '请输入景区名称', trigger: 'blur' }],
        base_id: [{ required: true, message: '请选择所属基地', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }]
      },
      SCENIC_STATUS,
      // 所属基地下拉（景区、小区表单用）：接口返回 + 前端追加「公共景区（不挂基地）」值 0
      baseOptions: [],
      // 等级下拉：字典 scenic_level 的 value（5A/4A…），未播种字典前为空
      levelOptions: [],
      sceneryTypes: ['自然风光', '乡村度假', '历史人文', '主题乐园'],
      // 省市区三级联动（走公共接口，值绑区域编码提交）
      provinces: [],
      cities: [],
      areas: []
    }
  },
  computed: {
    isEdit() { return !!(this.currentRow && this.currentRow.id) }
  },
  created() {
    this.loadData()
    this.loadBaseOptions()
    this.loadLevelOptions()
  },
  methods: {
    // 列表：服务端分页 + 筛选（keyword 模糊 / base_id 精确含 0 / status 精确），排序后端保证
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const keyword = (this.query.keyword || '').trim()
      if (keyword) params.keyword = keyword
      if (this.query.base_id !== '' && this.query.base_id !== null) params.base_id = this.query.base_id
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getScenicList(params).then(res => {
        if (res.code !== 200) return
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    // 所属基地下拉（含已下架标注）
    loadBaseOptions() {
      getStayBaseOptions().then(res => {
        if (res.code !== 200) return
        const arr = (res.data || []).map(b => ({
          value: b.id,
          label: b.status ? b.name : `${b.name}（已下架）`
        }))
        this.baseOptions = [{ value: 0, label: '公共景区（不挂基地）' }].concat(arr)
      }).catch(() => {})
    },
    // 等级下拉：字典 scenic_level（text 风格，value = 5A/4A…）
    loadLevelOptions() {
      getDictMap({ codes: 'scenic_level' }).then(res => {
        if (res.code !== 200) return
        const items = (res.data && res.data.scenic_level) || []
        this.levelOptions = items.map(d => ({ label: `${d.label}级景区`, value: d.value }))
      }).catch(() => {})
    },
    statusCount(val) { return this.list.filter(i => i.status === val).length },
    scenicStatusText,
    scenicStatusTagType,
    ticketText,
    baseLabel(id) {
      const item = (this.baseOptions || []).find(o => o.value === id)
      return item ? item.label : (id === 0 ? '公共景区' : String(id == null ? '' : id))
    },
    regionText(row) {
      return [row.province, row.city, row.district].filter(Boolean).join(' / ') || '—'
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', base_id: '', status: '' }
      this.page = 1
      this.loadData()
    },
    handleAdd() {
      this.currentRow = null
      this.form = this.emptyForm()
      this.formVisible = true
      this.initRegion()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.currentRow = Object.assign({}, row)
      const form = Object.assign(this.emptyForm(), row)
      form.base_id = (row.base_id === 0 || row.base_id) ? row.base_id : null
      form.features = Array.isArray(row.features) ? row.features.slice() : []
      this.form = form
      this.formVisible = true
      // 省市区按 code 逐级带出下级选项
      this.initRegion()
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    // ===== 地点搜索与省市区联动（与活动表单同款：腾讯地图关键词输入提示） =====
    // 输入景区名称 → 调公共接口 /address/keyword 出候选，选中回填省市区编码 + 详细地址 + 经纬度
    searchPlace(query, cb) {
      const keyword = (query || '').trim()
      if (!keyword) { cb([]); return }
      tencentAddressKeyword({ keyword }).then(res => {
        if (res.code !== 200 || !Array.isArray(res.data)) { cb([]); return }
        cb(res.data.map(item => ({ value: item.title || '', raw: item })))
      }).catch(() => cb([]))
    },
    // 选中候选地点：回填经纬度/详细地址/省市区编码，按腾讯中文名反查区域编码逐级联动
    onPlaceSelect(item) {
      const p = (item && item.raw) || {}
      const ad = p.ad_info || {}
      if (p.title) this.form.name = p.title
      if (p.location) {
        this.form.lat = p.location.lat
        this.form.lng = p.location.lng
      }
      this.form.address = p.address || ''
      this.form.province = ''
      this.form.city = ''
      this.form.district = ''
      this.cities = []
      this.areas = []
      const prov = this.matchRegion(this.provinces, ad.province || p.province)
      if (!prov) return
      this.form.province = prov.code
      this.loadCities(prov.code, () => {
        const city = this.matchRegion(this.cities, ad.city || p.city)
        if (!city) return
        this.form.city = city.code
        this.loadAreas(city.code, () => {
          const area = this.matchRegion(this.areas, ad.district || p.district)
          if (area) this.form.district = area.code
        })
      })
    },
    // 区域反查：先精确匹配，再包容匹配（腾讯全称「广西壮族自治区」↔ 表里「广西」等口径差异）
    matchRegion(list, name) {
      const target = (name || '').trim()
      if (!target) return null
      let hit = list.find(i => i.name === target)
      if (!hit) {
        hit = list.find(i => i.name.indexOf(target) !== -1 || target.indexOf(i.name) !== -1)
      }
      return hit || null
    },
    // ===== 省市区联动：值绑区域编码，编辑回显按 code 逐级带出 =====
    initRegion() {
      getProvince().then(res => {
        this.provinces = res.data || []
        this.cities = []
        this.areas = []
        const p = this.provinces.find(i => i.code === this.form.province)
        if (p) {
          this.loadCities(p.code, () => {
            const c = this.cities.find(i => i.code === this.form.city)
            if (c) this.loadAreas(c.code)
          })
        }
      }).catch(() => {})
    },
    loadCities(code, done) {
      getCity(code).then(res => {
        this.cities = res.data || []
        if (done) done()
      }).catch(() => {})
    },
    loadAreas(code, done) {
      getArea(code).then(res => {
        this.areas = res.data || []
        if (done) done()
      }).catch(() => {})
    },
    onProvinceChange(code) {
      this.form.city = ''
      this.form.district = ''
      this.cities = []
      this.areas = []
      const p = this.provinces.find(i => i.code === code)
      if (p) this.loadCities(p.code)
    },
    onCityChange(code) {
      this.form.district = ''
      this.areas = []
      const c = this.cities.find(i => i.code === code)
      if (c) this.loadAreas(c.code)
    },
    // ===== 操作 =====
    handleSortChange(row, val) {
      if (val === undefined || val === null) return
      setScenicSort(row.id, { sort: val }).then(res => {
        if (res.code === 200) {
          this.$message.success('排序已更新')
          this.loadData()
        }
      })
    },
    handleStatus(row, status) {
      const action = status === 1 ? '展示' : '下架'
      this.$confirm(`确认${action}「${row.name}」？${status === 1 ? '展示后小程序端可见。' : '下架后小程序端不再展示。'}`, action + '确认', { type: 'warning' })
        .then(() => {
          setScenicStatus(row.id, { status }).then(res => {
            if (res.code === 200) {
              this.$message.success('已' + action)
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    handleDelete(row) {
      this.$confirm(`确认删除「${row.name || '该景区'}」？`, '删除确认', { type: 'warning' })
        .then(() => {
          deleteScenic(row.id).then(res => {
            if (res.code === 200) {
              this.$message.success('已删除')
              this.loadData()
            }
          })
        }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const payload = Object.assign({}, this.form)
        // base_id 0（公共景区）合法，原样提交；features 已是字符串数组
        this.submitting = true
        const request = payload.id ? updateScenic(payload.id, payload) : createScenic(payload)
        request.then(res => {
          if (res.code !== 200) return
          this.$message.success(payload.id ? '已保存' : '已新增')
          this.formVisible = false
          this.loadData()
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    emptyForm() {
      return {
        id: 0,
        base_id: null,
        name: '',
        cover: '',
        level: '',
        slogan: '',
        open_time: '',
        ticket_price: 0,
        features: [],
        scenery_type: '',
        province: '', city: '', district: '', address: '',
        lng: '', lat: '',
        best_season: '',
        play_duration: '',
        intro: '',
        sort: 0,
        status: 1
      }
    }
  }
}
</script>

<style scoped>
.sort-input {
  width: 90px;
}
.sort-input >>> .el-input__inner {
  padding-left: 6px;
  padding-right: 34px;
  text-align: center;
}
.cover-thumb {
  width: 80px;
  height: 48px;
  border-radius: 4px;
  overflow: hidden;
  background: #f5f6f8;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}
.cover-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.cover-thumb--empty {
  color: #c0c4cc;
  font-size: 12px;
  border: 1px dashed #dcdfe6;
  background: #fafafa;
}
.level-tag {
  margin-left: 6px;
}

/* 地图地点联想候选（slot 内容在组件作用域内，scoped 生效） */
.place-suggestion {
  padding: 6px 0;
  line-height: 20px;
}
.ps-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
.ps-pin {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: var(--primary, #8E1103);
}
.ps-addr {
  margin: 4px 0 0 16px;
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 分段标题（与其他模块一致） */
.form-section {
  margin-bottom: 18px;
  padding-bottom: 4px;
}
.form-section-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--primary);
  border-left: 3px solid var(--primary);
  padding-left: 8px;
  margin: 16px 0 14px;
}
.form-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

/* 弹框内整体样式（与全局弹框适配） */
.scenic-form-dialog >>> .el-dialog__body {
  padding: 14px 30px 4px;
}
.scenic-form-dialog >>> .wechat-editor {
  min-height: 280px;
}
</style>

<style>
/* 地点联想下拉挂在 body 上，scoped 命中不了，这里用唯一 popper-class 限定范围做全局覆盖 */
.place-suggestion-popper .el-autocomplete-suggestion__wrap {
  padding: 6px 0;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(22, 4, 0, 0.12);
}
.place-suggestion-popper .el-autocomplete-suggestion__list li {
  padding: 4px 16px;
  line-height: 20px;
}
.place-suggestion-popper .el-autocomplete-suggestion__list li:hover,
.place-suggestion-popper .el-autocomplete-suggestion__list li.highlighted {
  background: rgba(142, 17, 3, 0.06);
}
.place-suggestion-popper .popper__arrow {
  display: none;
}
</style>
